import { Calendar } from 'lucide-react';

import hexagonPattern from '@/icons/hexagon-geometric.webp';
import xLogoWhite from '@/icons/x-team.svg';

import { MONTHLY_TOP_UP_AMOUNT } from '@/constants/dashboard';

import { formatCurrency, formatCurrencyForDisplay, formatDateFriendly } from '@/utils/formatting';

interface BalanceCardProps {
  balance: number;
  nextTopUpDate: string | null;
  hasAnimated?: boolean;
  isVisible?: boolean;
}

function BalanceCard({
  balance,
  nextTopUpDate,
  hasAnimated = true,
  isVisible = true,
}: BalanceCardProps) {
  const { integerPart, decimalPart } = formatCurrencyForDisplay(balance);

  return (
    <div
      data-testid="balance-card"
      className={`relative flex h-full min-h-[200px] flex-col overflow-hidden rounded-xl bg-navy-950 p-6 shadow-lg transition-all duration-700 ${
        hasAnimated ? '' : isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
      }`}
    >
      <img
        src={hexagonPattern}
        alt=""
        className="absolute inset-0 size-full object-cover opacity-[0.15]"
      />

      <img src={xLogoWhite} alt="X-Team" className="absolute top-5 right-5 size-12" />

      <p
        className="relative mb-2 text-xs font-semibold tracking-wider text-gray-400 uppercase"
        data-testid="total-balance-label"
      >
        Total Balance
      </p>
      <h2 className="relative text-4xl font-bold text-white" data-testid="total-balance-amount">
        ${integerPart}
        <span className="text-xl">.{decimalPart}</span>
      </h2>

      <div className="flex-1"></div>

      <div className="relative mb-4 border-t border-white/10"></div>

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-300">
          <Calendar className="size-4 text-red-500" />
          <span className="text-sm" data-testid="next-top-up">
            Next top-up: {nextTopUpDate ? formatDateFriendly(nextTopUpDate) : 'January 2027'}
          </span>
        </div>
        <span className="font-semibold text-brand-green">
          + {formatCurrency(MONTHLY_TOP_UP_AMOUNT, true)}
        </span>
      </div>
    </div>
  );
}

export { BalanceCard };
